import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Button,
  Typography,
  CircularProgress,
  TextField,
  Stack,
  IconButton,
  Chip,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { Plus, Trash2, X, Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { api, diagnoses } from '../../lib/api';
import { safeConsole } from '../../lib/piiProtection';
import { PharmacySelector } from './PharmacySelector';
import { SaveTemplateDialog } from './SaveTemplateDialog';
import { TemplateSelectorDialog } from './TemplateSelectorDialog';
import { DiagnosisSuggestionsPanel } from './DiagnosisSuggestionsPanel';
import { SymptomDiagnosisPanel } from './SymptomDiagnosisPanel';
import { PrescriptionSafetyCheck } from './PrescriptionSafetyCheck';
import { PrescribingReferencePanel } from './PrescribingReferencePanel';
import { aiDiagnosisTemplates } from '../../lib/api/modules/aiFeatures';
import type { DiagnosisMedicationLine } from '../../lib/api/modules/aiFeatures';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import type { Medication, LabTest, MedicationFrequency, CommonDiagnosis, PrescriptionTemplate } from '../../types';

const COMMON_LAB_TESTS = [
  'Complete Blood Count (CBC)',
  'Blood Sugar (Fasting)',
  'Blood Sugar (PP)',
  'HbA1c',
  'Lipid Profile',
  'Liver Function Test (LFT)',
  'Kidney Function Test (KFT)',
  'Thyroid Profile (T3, T4, TSH)',
  'Urine Routine',
  'Stool Routine',
  'Chest X-Ray',
  'ECG',
  'Vitamin D',
  'Vitamin B12',
  'Ultrasound Abdomen',
  'Serum Creatinine',
  'Serum Uric Acid',
  'Serum Electrolytes',
  'ESR',
  'CRP',
  'COVID-19 Antigen',
  'Dengue NS1 Antigen',
  'Malaria Antigen',
  'Widal Test',
];

interface PrescriptionWizardProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  visitId?: string;
  onSuccess?: () => void;
  mode?: 'create' | 'reissue';
  reissueOfPrescriptionId?: string;
  initialPrescription?: {
    chiefComplaint?: string;
    diagnosis?: string;
    clinicalNotes?: string;
    followUpDays?: number;
    labTests?: string[];
    advice?: string;
    followUpInstructions?: string;
    medications?: Array<{
      medicationName?: string;
      dosage?: string;
      frequency?: string;
      duration?: string;
      instructions?: string;
    }>;
  };
  isInline?: boolean;
  /** Open the wizard pre-populated from an existing DRAFT prescription */
  editDraftId?: string;
}

const FREQ_CHIPS = [
  { label: 'OD',  title: 'Once Daily',        value: 'Once daily' as MedicationFrequency },
  { label: 'BD',  title: 'Twice Daily',        value: 'Twice daily' as MedicationFrequency },
  { label: 'TDS', title: 'Three Times Daily',  value: 'Three times daily' as MedicationFrequency },
  { label: 'QID', title: 'Four Times Daily',   value: 'Four times daily' as MedicationFrequency },
  { label: 'SOS', title: 'When Needed',        value: 'SOS (When needed)' as MedicationFrequency },
];

const DUR_PRESETS = ['3 days', '5 days', '7 days', '10 days', '14 days'];

const EMPTY_MED = (): Omit<Medication, 'id'> => ({
  name: '',
  dosage: '',
  frequency: 'Twice daily',
  duration: '',
  instructions: '',
  timing: ['Morning', 'Night'],
});

export function PrescriptionWizard({
  open,
  onClose,
  patientId,
  patientName,
  visitId,
  onSuccess,
  mode = 'create',
  reissueOfPrescriptionId,
  initialPrescription,
  isInline = false,
  editDraftId,
}: PrescriptionWizardProps) {
  const { userData, clinic } = useAuth();
  const { can } = usePermissions();
  const isReissue = mode === 'reissue';
  const clinicId = userData?.clinicId || '';
  const canRunSafetyCheck = userData?.role === 'doctor' || userData?.role === 'clinic_admin';
  const canSuggestByDiagnosis = userData?.role === 'doctor' && can('AI_TEMPLATE_RECOMMENDATION');
  // Separate permission (AI_DIFFERENTIAL_DIAGNOSIS) — a clinic/super admin can grant or revoke
  // "suggest a diagnosis" independently of "suggest medications for a diagnosis".
  const canSuggestDiagnosis = userData?.role === 'doctor' && can('AI_DIFFERENTIAL_DIAGNOSIS');

  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [changeReason, setChangeReason] = useState('');
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showDiagnosisSuggestions, setShowDiagnosisSuggestions] = useState(false);
  const [showSymptomDiagnosis, setShowSymptomDiagnosis] = useState(false);
  /** null = not checked yet, true = an approved (free) template exists, false = none found. */
  const [hasApprovedTemplate, setHasApprovedTemplate] = useState<boolean | null>(null);

  const [chiefComplaints, setChiefComplaints] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [clinicalNotes, setClinicalNotes] = useState('');

  const [medications, setMedications] = useState<Omit<Medication, 'id'>[]>([EMPTY_MED()]);

  const [labTests, setLabTests] = useState<Omit<LabTest, 'id'>[]>([]);
  const [labTestInput, setLabTestInput] = useState('');
  const [followUpDays, setFollowUpDays] = useState<number | ''>('');
  const [advice, setAdvice] = useState('');
  const [followUpInstructions, setFollowUpInstructions] = useState('');

  const [sendToPharmacy, setSendToPharmacy] = useState(false);
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<string>('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [attachedLabDocs, setAttachedLabDocs] = useState<Array<{
    filePath: string;
    fileName: string;
    sessionId: string;
    verified: boolean;
  }>>([]);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Per-row medication search state
  const [searchTerms, setSearchTerms] = useState<Record<number, string>>({});
  const [activeSearchRow, setActiveSearchRow] = useState<number | null>(null);
  const activeSearch = activeSearchRow !== null ? (searchTerms[activeSearchRow] || '') : '';

  // Medication search query
  const { data: medOptions = [], isFetching: fetchingMeds } = useQuery({
    queryKey: ['rx-med-search', clinicId, activeSearch],
    queryFn: () =>
      axios
        .get(`/api/medications/clinic/${clinicId}/search?q=${encodeURIComponent(activeSearch)}`)
        .then((r) => r.data?.data || []),
    enabled: activeSearch.length >= 2 && !!clinicId,
    staleTime: 60_000,
  });

  const [commonDiagnoses, setCommonDiagnoses] = useState<CommonDiagnosis[]>([]);

  useEffect(() => {
    diagnoses
      .list()
      .then((r) => {
        if (r.data) setCommonDiagnoses(r.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setIsDirty(true);
  }, [diagnosis, medications, chiefComplaints, clinicalNotes, labTests, followUpDays, advice, followUpInstructions]);

  useEffect(() => {
    if (!open && !isInline) resetForm();
  }, [open, isInline]);

  useEffect(() => {
    if (open && initialPrescription) {
      setChiefComplaints(initialPrescription.chiefComplaint || '');
      setDiagnosis(initialPrescription.diagnosis || '');
      setClinicalNotes(initialPrescription.clinicalNotes || '');
      setFollowUpDays(initialPrescription.followUpDays ?? '');
      if (initialPrescription.medications?.length) {
        setMedications(
          initialPrescription.medications.map((m) => ({
            name: m.medicationName || '',
            dosage: m.dosage || '',
            frequency: (m.frequency as MedicationFrequency) || 'Twice daily',
            duration: m.duration || '',
            instructions: m.instructions || '',
            timing: [],
          }))
        );
      }
      if (initialPrescription.labTests?.length) {
        setLabTests(initialPrescription.labTests.map((name) => ({ name })));
      }
      if (initialPrescription.advice) setAdvice(initialPrescription.advice);
      if (initialPrescription.followUpInstructions) setFollowUpInstructions(initialPrescription.followUpInstructions);
    }
  }, [open, initialPrescription]);

  useEffect(() => {
    if (open && !isReissue) loadDraft();
  }, [open, visitId, editDraftId]);

  // Proactive/real-time: as the diagnosis field stabilizes, silently check whether a free
  // approved-template match exists (never touches the paid AI fallback — see
  // aiDiagnosisTemplates.check). Surfaces a badge the doctor can tap; nothing is auto-applied.
  useEffect(() => {
    if (!canSuggestByDiagnosis || !diagnosis.trim() || diagnosis.trim().length < 3) {
      setHasApprovedTemplate(null);
      return;
    }
    const timer = setTimeout(() => {
      aiDiagnosisTemplates.check(diagnosis.trim())
        .then((res) => setHasApprovedTemplate(!!res.data?.medications?.length))
        .catch(() => setHasApprovedTemplate(null));
    }, 700);
    return () => clearTimeout(timer);
  }, [diagnosis, canSuggestByDiagnosis]);

  const resetForm = () => {
    setChiefComplaints('');
    setDiagnosis('');
    setClinicalNotes('');
    setMedications([EMPTY_MED()]);
    setLabTests([]);
    setLabTestInput('');
    setFollowUpDays('');
    setAdvice('');
    setFollowUpInstructions('');
    setSendToPharmacy(false);
    setSelectedPharmacyId('');
    setDeliveryNotes('');
    setAttachedLabDocs([]);
    setIsDirty(false);
    setChangeReason('');
    setDraftId(null);
    setFieldErrors({});
    setSearchTerms({});
  };

  const loadDraft = async () => {
    try {
      let draft: any = null;

      if (editDraftId) {
        const res = await api.prescriptions.getById(editDraftId);
        if (res.data?.status === 'DRAFT') draft = res.data;
      } else if (visitId) {
        const res = await api.prescriptions.getByVisit(visitId);
        const list = Array.isArray(res.data) ? res.data : [];
        draft = list.find((p: any) => p.status === 'DRAFT') ?? null;
      } else if (patientId) {
        const res = await api.prescriptions.getByPatient(patientId);
        const list = Array.isArray(res.data) ? res.data : [];
        draft = list.find((p: any) => p.status === 'DRAFT') ?? null;
      }

      if (!draft) return;

      setDraftId(draft.id);
      setChiefComplaints(draft.chiefComplaint || '');
      setDiagnosis(draft.diagnosis || '');
      setClinicalNotes(draft.clinicalNotes || '');
      if (draft.followUpDays) setFollowUpDays(draft.followUpDays);

      const rawMeds = (draft.medications?.length ? draft.medications : draft.items) || [];
      const loadedMeds = rawMeds
        .filter((m: any) => m.medicationName || m.medicine)
        .map((m: any) => ({
          name: m.medicationName || m.medicine || '',
          dosage: m.dosage || '',
          frequency: (m.frequency as MedicationFrequency) || 'Twice daily',
          duration: m.duration || '',
          instructions: m.instructions || m.notes || '',
          timing: [],
        }));

      if (loadedMeds.length > 0) setMedications(loadedMeds);
      setIsDirty(false);
    } catch (err) {
      safeConsole.log('No draft prescription found');
    }
  };

  const saveDraft = async (): Promise<string | null> => {
    try {
      const validMedications = medications.filter((m) => m.name.trim());
      if (validMedications.length === 0) return null;

      const payload = {
        visitId: visitId || '',
        patientId,
        doctorId: userData?.id || '',
        diagnosis: diagnosis.trim() || 'Draft in progress',
        chiefComplaint: chiefComplaints.trim() || undefined,
        clinicalNotes: clinicalNotes.trim() || undefined,
        items: validMedications.map((m) => ({
          medicine: m.name,
          dosage: m.dosage,
          frequency: m.frequency,
          duration: m.duration,
          notes: m.instructions || undefined,
        })),
        labDocuments:
          attachedLabDocs.length > 0
            ? attachedLabDocs.map((doc) => ({
                filePath: doc.filePath,
                fileName: doc.fileName,
                sessionId: doc.sessionId,
              }))
            : undefined,
        status: 'DRAFT',
      };

      if (!draftId) {
        const res = await api.prescriptions.create(payload);
        if (res.data?.id) {
          setDraftId(res.data.id);
          return res.data.id;
        }
        return null;
      } else {
        await api.prescriptions.updateDraft(draftId, payload);
        return draftId;
      }
    } catch (err) {
      safeConsole.error('Failed to save draft:', err);
      return null;
    }
  };

  const handleLoadTemplate = (template: any) => {
    if (template.chiefComplaint) setChiefComplaints(template.chiefComplaint);
    if (template.diagnosis) setDiagnosis(template.diagnosis);
    if (template.clinicalNotes) setClinicalNotes(template.clinicalNotes);
    if (template.medications?.length) {
      setMedications(
        template.medications.map((med: any) => ({
          name: med.medicationName || med.name || '',
          dosage: med.dosage || '',
          frequency: (med.frequency as MedicationFrequency) || 'Twice daily',
          duration: med.duration || '5-7 days',
          instructions: med.instructions || med.notes || 'As prescribed',
          timing: med.timing || ['Morning', 'Night'],
        }))
      );
    }
    setFieldErrors({});
    toast.success(`Template "${template.name}" loaded successfully!`);
  };

  /**
   * Apply diagnosis-driven suggestions into the medications list. Appends to existing
   * non-empty rows (replacing the initial blank row). AI lines are flagged for the audit
   * trail; the doctor still reviews/edits and the safety check runs on issue.
   */
  const handleApplySuggestions = (
    lines: DiagnosisMedicationLine[],
    meta: { source: 'APPROVED_TEMPLATE' | 'AI' }
  ) => {
    const mapped: Omit<Medication, 'id'>[] = lines.map((l) => ({
      name: l.name,
      dosage: l.dosage || '',
      frequency: (l.frequency as MedicationFrequency) || 'Twice daily',
      duration: l.duration || '',
      instructions: l.instructions || '',
      timing: ['Morning', 'Night'],
      medicationMasterId: l.medicationMasterId || undefined,
      isAiRecommended: meta.source === 'AI',
      aiRecommendationReason: l.rationale || undefined,
    }));
    setMedications((prev) => {
      const existing = prev.filter((m) => m.name.trim());
      return existing.length > 0 ? [...existing, ...mapped] : mapped;
    });
    setFieldErrors((prev) => ({ ...prev, medications: '' }));
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!diagnosis.trim()) errors.diagnosis = 'Diagnosis is required';
    if (medications.filter((m) => m.name.trim()).length === 0)
      errors.medications = 'At least one medication is required';
    if (sendToPharmacy && !selectedPharmacyId) errors.pharmacy = 'Please select a pharmacy';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error('Please fill all required fields');
      return false;
    }
    return true;
  };

  const handleSaveAsDraft = async () => {
    if (isReissue) {
      toast.error('Draft save is not available while reissuing');
      return;
    }
    setLoading(true);
    try {
      const validMedications = medications.filter((m) => m.name.trim());
      const res = await api.prescriptions.create({
        visitId: visitId || '',
        patientId,
        doctorId: userData?.id || '',
        diagnosis: diagnosis.trim(),
        chiefComplaint: chiefComplaints.trim() || undefined,
        clinicalNotes: clinicalNotes.trim() || undefined,
        items: validMedications.map((m) => ({
          medicine: m.name,
          dosage: m.dosage,
          frequency: m.frequency,
          duration: m.duration,
          notes: m.instructions || undefined,
        })),
        labDocuments:
          attachedLabDocs.length > 0
            ? attachedLabDocs.map((doc) => ({
                filePath: doc.filePath,
                fileName: doc.fileName,
                sessionId: doc.sessionId,
              }))
            : undefined,
        followUpDays: followUpDays !== '' ? followUpDays : undefined,
        followUpInstructions: followUpInstructions.trim() || undefined,
        labTests: labTests.length > 0 ? labTests.map((t) => t.name) : undefined,
      });
      if (res.data?.id) setDraftId(res.data.id);
      toast.success('Prescription saved as draft');
      handleClose();
      onSuccess?.();
    } catch (err) {
      safeConsole.error('Failed to save draft:', err);
      toast.error('Failed to save prescription');
    } finally {
      setLoading(false);
    }
  };

  const handleIssueToPatient = async () => {
    if (!validate()) return;
    if (userData?.role !== 'doctor') {
      toast.error('Unauthorized: Only doctors can issue prescriptions');
      return;
    }
    setLoading(true);
    try {
      const validMedications = medications.filter((m) => m.name.trim());

      if (isReissue) {
        if (!reissueOfPrescriptionId) throw new Error('Original prescription is missing');
        if (!changeReason.trim()) {
          toast.error('Please provide a change reason');
          return;
        }
        const response = await api.prescriptions.reissue({
          prescriptionId: reissueOfPrescriptionId,
          chiefComplaint: chiefComplaints.trim() || undefined,
          diagnosis: diagnosis.trim() || undefined,
          clinicalNotes: clinicalNotes.trim() || undefined,
          followUpDays: followUpDays !== '' ? followUpDays : undefined,
          followUpInstructions: followUpInstructions.trim() || undefined,
        labTests: labTests.length > 0 ? labTests.map((t) => t.name) : undefined,
          changeReason: changeReason.trim(),
          replaceAllMedications: true,
          medications: validMedications.map((m) => ({
            medicationName: m.name,
            dosage: m.dosage,
            frequency: m.frequency,
            duration: m.duration,
            instructions: m.instructions,
          })),
        });
        if (response.error) throw new Error(response.error);
        toast.success('Prescription reissued');
        handleClose();
        onSuccess?.();
      } else {
        let prescriptionId = draftId;
        if (!prescriptionId) {
          const response = await api.prescriptions.create({
            visitId: visitId || '',
            patientId,
            doctorId: userData?.id || '',
            diagnosis: diagnosis.trim(),
            chiefComplaint: chiefComplaints.trim() || undefined,
            clinicalNotes: clinicalNotes.trim() || undefined,
            items: validMedications.map((m) => ({
              medicine: m.name,
              dosage: m.dosage,
              frequency: m.frequency,
              duration: m.duration,
              notes: m.instructions || undefined,
            })),
            labDocuments: attachedLabDocs.map((doc) => ({
              filePath: doc.filePath,
              fileName: doc.fileName,
              sessionId: doc.sessionId,
            })),
            followUpDays: followUpDays !== '' ? followUpDays : undefined,
            followUpInstructions: followUpInstructions.trim() || undefined,
        labTests: labTests.length > 0 ? labTests.map((t) => t.name) : undefined,
          });
          prescriptionId = response.data?.id || null;
        }
        if (prescriptionId) await api.prescriptions.issue(prescriptionId);
        toast.success('Prescription issued to patient');
        handleClose();
        onSuccess?.();
      }
    } catch (err) {
      safeConsole.error('Failed to issue prescription:', err);
      toast.error('Failed to issue prescription');
    } finally {
      setLoading(false);
    }
  };

  const handleIssueAndSend = async () => {
    if (!validate()) return;
    if (userData?.role !== 'doctor') {
      toast.error('Unauthorized: Only doctors can issue prescriptions');
      return;
    }
    setLoading(true);
    try {
      const validMedications = medications.filter((m) => m.name.trim());

      if (isReissue) {
        if (!reissueOfPrescriptionId) throw new Error('Original prescription is missing');
        if (!changeReason.trim()) {
          toast.error('Please provide a change reason');
          return;
        }
        const response = await api.prescriptions.reissue({
          prescriptionId: reissueOfPrescriptionId,
          chiefComplaint: chiefComplaints.trim() || undefined,
          diagnosis: diagnosis.trim() || undefined,
          clinicalNotes: clinicalNotes.trim() || undefined,
          followUpDays: followUpDays !== '' ? followUpDays : undefined,
          followUpInstructions: followUpInstructions.trim() || undefined,
        labTests: labTests.length > 0 ? labTests.map((t) => t.name) : undefined,
          changeReason: changeReason.trim(),
          replaceAllMedications: true,
          medications: validMedications.map((m) => ({
            medicationName: m.name,
            dosage: m.dosage,
            frequency: m.frequency,
            duration: m.duration,
            instructions: m.instructions,
          })),
        });
        if (response.error) throw new Error(response.error);
        toast.success('Prescription reissued');
        handleClose();
        onSuccess?.();
        return;
      }

      let prescriptionId = draftId;
      if (!prescriptionId) {
        const response = await api.prescriptions.create({
          visitId: visitId || '',
          patientId,
          doctorId: userData?.id || '',
          diagnosis: diagnosis.trim(),
          chiefComplaint: chiefComplaints.trim() || undefined,
          clinicalNotes: clinicalNotes.trim() || undefined,
          items: validMedications.map((m) => ({
            medicine: m.name,
            dosage: m.dosage,
            frequency: m.frequency,
            duration: m.duration,
            notes: m.instructions || undefined,
          })),
          labDocuments: attachedLabDocs.map((doc) => ({
            filePath: doc.filePath,
            fileName: doc.fileName,
            sessionId: doc.sessionId,
          })),
          followUpDays: followUpDays !== '' ? followUpDays : undefined,
          followUpInstructions: followUpInstructions.trim() || undefined,
        labTests: labTests.length > 0 ? labTests.map((t) => t.name) : undefined,
        });
        prescriptionId = response.data?.id || null;
      }

      if (!prescriptionId) throw new Error('Prescription ID not returned');
      await api.prescriptions.issue(prescriptionId);

      if (sendToPharmacy && selectedPharmacyId) {
        await api.prescriptions.sendToPharmacy(prescriptionId, selectedPharmacyId, deliveryNotes);
        toast.success('Prescription issued and sent to pharmacy');
      } else {
        toast.success('Prescription issued to patient');
      }

      handleClose();
      onSuccess?.();
    } catch (err) {
      safeConsole.error('Failed to issue and send prescription:', err);
      toast.error('Failed to complete action');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Medication row helpers
  const updateMed = (index: number, patch: Partial<Omit<Medication, 'id'>>) => {
    setMedications(medications.map((m, i) => (i === index ? { ...m, ...patch } : m)));
    setFieldErrors((prev) => ({ ...prev, medications: '' }));
  };

  const removeMed = (index: number) => {
    setMedications(medications.filter((_, i) => i !== index));
    setSearchTerms((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const addLabTest = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setLabTests([...labTests, { name: trimmed }]);
    setLabTestInput('');
  };

  // ── Form content ──────────────────────────────────────────────────────────────
  const formContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: 3 }}>

      {/* Reissue change reason */}
      {isReissue && (
        <TextField
          fullWidth
          size="small"
          label="Change reason *"
          value={changeReason}
          onChange={(e) => setChangeReason(e.target.value)}
          placeholder="Why is this prescription being reissued?"
        />
      )}

      {/* Row 1: Chief Complaint + Diagnosis */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
        <Box sx={{ width: '100%' }}>
          <TextField
            label="Chief Complaint"
            multiline
            rows={2}
            size="small"
            fullWidth
            value={chiefComplaints}
            onChange={(e) => setChiefComplaints(e.target.value)}
            placeholder="e.g. Fever, cough for 3 days"
          />
          {canSuggestDiagnosis && (
            <Button
              size="small"
              startIcon={<Sparkles size={14} />}
              onClick={() => {
                if (!chiefComplaints.trim()) {
                  toast.error('Describe the chief complaint first');
                  return;
                }
                setShowSymptomDiagnosis(true);
              }}
              sx={{ mt: 0.75 }}
            >
              Suggest diagnosis
            </Button>
          )}
        </Box>
        <Box sx={{ width: '100%' }}>
          <Autocomplete
            freeSolo
            fullWidth
            size="small"
            options={commonDiagnoses.map((d) => d.name)}
            value={diagnosis}
            onInputChange={(_, v) => {
              setDiagnosis(v);
              setFieldErrors((prev) => ({ ...prev, diagnosis: '' }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Diagnosis *"
                error={!!fieldErrors.diagnosis}
                helperText={fieldErrors.diagnosis}
              />
            )}
          />
          {canSuggestByDiagnosis && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
              <Button
                size="small"
                startIcon={<Sparkles size={14} />}
                onClick={() => {
                  if (!diagnosis.trim()) {
                    toast.error('Enter a diagnosis first');
                    return;
                  }
                  setShowDiagnosisSuggestions(true);
                }}
              >
                Suggest medications
              </Button>
              {hasApprovedTemplate === true && (
                <Chip
                  label="Approved suggestions ready"
                  size="small"
                  color="success"
                  variant="outlined"
                  onClick={() => setShowDiagnosisSuggestions(true)}
                  sx={{ cursor: 'pointer', fontSize: 11 }}
                />
              )}
            </Stack>
          )}
        </Box>
      </Stack>

      {/* Row 2: Medications */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color={fieldErrors.medications ? 'error.main' : 'text.primary'}
          >
            Medications
            {fieldErrors.medications && (
              <Typography component="span" variant="caption" color="error" ml={1}>
                — {fieldErrors.medications}
              </Typography>
            )}
          </Typography>
          <Button
            size="small"
            startIcon={<Plus size={14} />}
            onClick={() => {
              setMedications([...medications, EMPTY_MED()]);
              setFieldErrors((prev) => ({ ...prev, medications: '' }));
            }}
            variant="outlined"
          >
            Add Med
          </Button>
        </Stack>

        <Stack spacing={1}>
          {medications.map((med, idx) => (
            <Box key={idx}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Autocomplete
                freeSolo
                size="small"
                sx={{ flex: 1, minWidth: 160 }}
                options={activeSearchRow === idx ? (medOptions as any[]) : []}
                loading={fetchingMeds && activeSearchRow === idx}
                getOptionLabel={(o: any) => (typeof o === 'string' ? o : o.name)}
                inputValue={searchTerms[idx] ?? med.name}
                onInputChange={(_, v) => {
                  setSearchTerms((prev) => ({ ...prev, [idx]: v }));
                  setActiveSearchRow(idx);
                  updateMed(idx, { name: v });
                }}
                onChange={(_, v) => {
                  const name = typeof v === 'string' ? v : (v as any)?.name || '';
                  // Drug-master matches carry a strength (e.g. "500mg"), not a diagnosis/patient-aware
                  // dosage — that's only ever a sound default when the doctor hasn't typed one yet.
                  // Actual dosing comes from diagnosis-template suggestions or the RAG reference below,
                  // never invented as a flat per-drug default.
                  const strength = typeof v !== 'string' ? (v as any)?.strength : undefined;
                  const dosage = med.dosage.trim() ? med.dosage : (strength || med.dosage);
                  const masterId = typeof v !== 'string' ? (v as any)?.id : undefined;
                  setSearchTerms((prev) => ({ ...prev, [idx]: name }));
                  updateMed(idx, { name, dosage, medicationMasterId: masterId });
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Medicine name" size="small" />
                )}
              />
              <TextField
                size="small"
                sx={{ width: 90 }}
                placeholder="Dosage"
                value={med.dosage}
                onChange={(e) => updateMed(idx, { dosage: e.target.value })}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={med.frequency}
                onChange={(_, v) => {
                  if (v) updateMed(idx, { frequency: v as MedicationFrequency });
                }}
                sx={{ flexShrink: 0 }}
              >
                {FREQ_CHIPS.map((f) => (
                  <Tooltip key={f.label} title={f.title} placement="top">
                    <ToggleButton
                      value={f.value}
                      sx={{ px: 1.2, py: 0.3, fontSize: 11, lineHeight: 1.4 }}
                    >
                      {f.label}
                    </ToggleButton>
                  </Tooltip>
                ))}
              </ToggleButtonGroup>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                {DUR_PRESETS.map((d) => (
                  <Chip
                    key={d}
                    label={d.replace(' days', 'd')}
                    size="small"
                    variant={med.duration === d ? 'filled' : 'outlined'}
                    color={med.duration === d ? 'primary' : 'default'}
                    onClick={() => updateMed(idx, { duration: med.duration === d ? '' : d })}
                    sx={{ cursor: 'pointer', fontSize: 11, height: 24 }}
                  />
                ))}
                <TextField
                  size="small"
                  sx={{ width: 72 }}
                  placeholder="days"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={med.duration.replace(' days', '')}
                  onChange={(e) =>
                    updateMed(idx, {
                      duration: e.target.value ? `${e.target.value} days` : '',
                    })
                  }
                />
              </Stack>
              <IconButton
                size="small"
                onClick={() => removeMed(idx)}
                disabled={medications.length === 1}
                sx={{ flexShrink: 0 }}
              >
                <Trash2 size={14} />
              </IconButton>
            </Stack>
            {/* RAG prescribing reference + patient-specific safety flags for this medicine (₹0, debounced) */}
            <PrescribingReferencePanel drug={med.name} patientId={patientId} />
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Safety Check — enabled as soon as any medication name is entered */}
      {canRunSafetyCheck && (
        <PrescriptionSafetyCheck
          prescriptionId={draftId}
          hasPermission={canRunSafetyCheck}
          disabled={!medications.some((m) => m.name.trim())}
          onAutoSave={saveDraft}
          autoRunSignal={medications
            .filter((m) => m.name.trim())
            .map((m) => `${m.name}|${m.dosage}|${m.frequency}`)
            .join(';')}
        />
      )}

      {/* Row 3: Clinical Notes */}
      <TextField
        label="Clinical Notes"
        multiline
        rows={2}
        size="small"
        fullWidth
        value={clinicalNotes}
        onChange={(e) => setClinicalNotes(e.target.value)}
        placeholder="Observations, examination findings..."
      />

      {/* Row 4: Lab Tests */}
      <Box>
        <Typography variant="subtitle2" fontWeight={600} mb={1}>
          Lab Tests
        </Typography>

        {/* Common tests — click to add */}
        <Stack direction="row" flexWrap="wrap" gap={0.75} mb={1.5}>
          {COMMON_LAB_TESTS.filter((t) => !labTests.some((l) => l.name === t)).map((t) => (
            <Chip
              key={t}
              label={t}
              size="small"
              variant="outlined"
              onClick={() => setLabTests([...labTests, { name: t }])}
              sx={{ cursor: 'pointer', fontSize: 11 }}
            />
          ))}
        </Stack>

        {/* Selected tests + custom input */}
        <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
          {labTests.map((t, i) => (
            <Chip
              key={i}
              label={t.name}
              size="small"
              color="primary"
              onDelete={() => setLabTests(labTests.filter((_, j) => j !== i))}
            />
          ))}
          <TextField
            size="small"
            sx={{ width: 200 }}
            placeholder="Type custom test..."
            value={labTestInput}
            onChange={(e) => setLabTestInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && labTestInput.trim()) {
                e.preventDefault();
                setLabTests([...labTests, { name: labTestInput.trim() }]);
                setLabTestInput('');
              }
            }}
            onBlur={() => {
              if (labTestInput.trim()) {
                setLabTests([...labTests, { name: labTestInput.trim() }]);
                setLabTestInput('');
              }
            }}
          />
        </Stack>
      </Box>

      {/* Row 5: Follow-up */}
      <Box>
        <Typography variant="subtitle2" fontWeight={600} mb={1}>
          Follow-up
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" gap={0.5}>
          {[3, 5, 7, 10, 14, 30].map((d) => (
            <Chip
              key={d}
              label={`${d}d`}
              size="small"
              variant={followUpDays === d ? 'filled' : 'outlined'}
              color={followUpDays === d ? 'primary' : 'default'}
              onClick={() => setFollowUpDays(followUpDays === d ? '' : d)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
          <TextField
            size="small"
            sx={{ width: 70 }}
            placeholder="other"
            type="number"
            inputProps={{ min: 1 }}
            value={
              typeof followUpDays === 'number' && ![3, 5, 7, 10, 14, 30].includes(followUpDays)
                ? followUpDays
                : ''
            }
            onChange={(e) => setFollowUpDays(e.target.value ? Number(e.target.value) : '')}
          />
          <Typography variant="body2" color="text.secondary">
            days
          </Typography>
        </Stack>
        <TextField
          size="small"
          fullWidth
          sx={{ mt: 1 }}
          placeholder="Follow-up instructions (optional)"
          value={followUpInstructions}
          onChange={(e) => setFollowUpInstructions(e.target.value)}
        />
      </Box>

      {/* Row 6: Pharmacy */}
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={sendToPharmacy}
              onChange={(e) => setSendToPharmacy(e.target.checked)}
              size="small"
            />
          }
          label="Send to Pharmacy"
        />
        {sendToPharmacy && (
          <Box sx={{ mt: 1 }}>
            <PharmacySelector
              selectedPharmacyId={selectedPharmacyId}
              onSelectPharmacy={setSelectedPharmacyId}
            />
            {fieldErrors.pharmacy && (
              <Typography variant="caption" color="error">
                {fieldErrors.pharmacy}
              </Typography>
            )}
            <TextField
              size="small"
              fullWidth
              sx={{ mt: 1 }}
              placeholder="Delivery notes (optional)"
              value={deliveryNotes}
              onChange={(e) => setDeliveryNotes(e.target.value)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );

  // ── Action bar ────────────────────────────────────────────────────────────────
  const actionBar = (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderTop: 1,
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} size="small">
          Cancel
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowSaveTemplateDialog(true)}
          disabled={loading}
        >
          Save Template
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {!isReissue && (
          <Button variant="outlined" size="small" onClick={handleSaveAsDraft} disabled={loading}>
            Save Draft
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          onClick={sendToPharmacy ? handleIssueAndSend : handleIssueToPatient}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} /> : undefined}
        >
          {sendToPharmacy ? 'Issue & Send' : 'Issue to Patient'}
        </Button>
      </Box>
    </Box>
  );

  // ── Inline mode ───────────────────────────────────────────────────────────────
  if (isInline) {
    return (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              px: 3,
              pt: 2,
              pb: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {isReissue ? 'Reissue Prescription' : 'New Prescription'} — {patientName}
              </Typography>
              {draftId && (
                <Chip label="Draft loaded" size="small" color="warning" variant="outlined" sx={{ mt: 0.5 }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowTemplateSelector(true)}
                disabled={loading}
              >
                Load Template
              </Button>
              <IconButton size="small" onClick={handleClose} disabled={loading}>
                <X size={18} />
              </IconButton>
            </Box>
          </Box>
          {formContent}
          {actionBar}
        </Box>
        <SaveTemplateDialog
          open={showSaveTemplateDialog}
          onClose={() => setShowSaveTemplateDialog(false)}
          patientName={patientName}
          diagnosis={diagnosis}
          chiefComplaints={chiefComplaints}
          clinicalNotes={clinicalNotes}
          medications={medications}
          doctorId={userData?.id || ''}
          clinicId={clinic?.id || ''}
        />
        <TemplateSelectorDialog
          open={showTemplateSelector}
          onClose={() => setShowTemplateSelector(false)}
          onSelectTemplate={(t: PrescriptionTemplate) => {
            handleLoadTemplate(t);
            setShowTemplateSelector(false);
          }}
          doctorId={userData?.id || ''}
        />
        <DiagnosisSuggestionsPanel
          open={showDiagnosisSuggestions}
          onClose={() => setShowDiagnosisSuggestions(false)}
          diagnosis={diagnosis}
          patientId={patientId}
          visitId={visitId}
          onApply={handleApplySuggestions}
          canApprove={canSuggestByDiagnosis}
        />
        <SymptomDiagnosisPanel
          open={showSymptomDiagnosis}
          onClose={() => setShowSymptomDiagnosis(false)}
          chiefComplaint={chiefComplaints}
          patientId={patientId}
          visitId={visitId}
          onApply={(d) => { setDiagnosis(d); setFieldErrors((prev) => ({ ...prev, diagnosis: '' })); }}
          canApprove={canSuggestDiagnosis}
        />
      </>
    );
  }

  // ── Dialog mode ───────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
      >
        {/* Dialog title bar */}
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexShrink: 0,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {isReissue ? 'Reissue Prescription' : 'New Prescription'} — {patientName}
            </Typography>
            {draftId && (
              <Chip label="Draft loaded" size="small" color="warning" variant="outlined" sx={{ mt: 0.5 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowTemplateSelector(true)}
              disabled={loading}
            >
              Load Template
            </Button>
            <IconButton size="small" onClick={handleClose} disabled={loading}>
              <X size={18} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0, overflowY: 'auto' }}>{formContent}</DialogContent>

        {actionBar}
      </Dialog>

      <SaveTemplateDialog
        open={showSaveTemplateDialog}
        onClose={() => setShowSaveTemplateDialog(false)}
        patientName={patientName}
        diagnosis={diagnosis}
        chiefComplaints={chiefComplaints}
        clinicalNotes={clinicalNotes}
        medications={medications}
        doctorId={userData?.id || ''}
        clinicId={clinic?.id || ''}
      />
      <TemplateSelectorDialog
        open={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        onSelectTemplate={(t: PrescriptionTemplate) => {
          handleLoadTemplate(t);
          setShowTemplateSelector(false);
        }}
        doctorId={userData?.id || ''}
      />
      <DiagnosisSuggestionsPanel
        open={showDiagnosisSuggestions}
        onClose={() => setShowDiagnosisSuggestions(false)}
        diagnosis={diagnosis}
        patientId={patientId}
        visitId={visitId}
        onApply={handleApplySuggestions}
        canApprove={canSuggestByDiagnosis}
      />
      <SymptomDiagnosisPanel
        open={showSymptomDiagnosis}
        onClose={() => setShowSymptomDiagnosis(false)}
        chiefComplaint={chiefComplaints}
        patientId={patientId}
        visitId={visitId}
        onApply={(d) => { setDiagnosis(d); setFieldErrors((prev) => ({ ...prev, diagnosis: '' })); }}
        canApprove={canSuggestDiagnosis}
      />
    </>
  );
}
